// The emoji Lantern offers, by Slack's names (a reaction is stored by name,
// so Slack apps and Lantern agree), in the picker's categories, from
// emojiData.ts. Their words help search find them.
import { emojiData } from "./emojiData";

export type Emoji = { name: string; glyph: string; words: string; others?: string[] };

// The picker's categories, each with the emoji that stands for it.
const icons: Record<string, string> = { people: "😀", nature: "🌱", food: "🍔", travel: "✈️", activity: "⚽", objects: "💡", symbols: "❤️", flags: "🏳️" };

export const categories = emojiData.map(([id]) => ({ id, icon: icons[id] ?? "🙂" }));

export const emoji: Emoji[] = emojiData.flatMap(([, list]) => list.map(([name, glyph, words, others]) => ({ name, glyph, words, others: others?.split(" ") })));

export const byCategory = new Map(emojiData.map(([id, list]) => [id, list.map(([name]) => name)]));

const glyphs = new Map(emoji.map((e) => [e.name, e.glyph]));
// Other names Slack accepts for the same emoji.
for (const [, list] of emojiData) for (const [name, , , others] of list) for (const alias of others?.split(" ") ?? []) if (!glyphs.has(alias)) glyphs.set(alias, glyphs.get(name)!);
for (const [alias, name] of [
  ["slightly_smiling", "slightly_smiling_face"],
  ["thinking", "thinking_face"],
  ["shrug", "man-shrugging"],
  ["party_popper", "tada"],
  ["check", "white_check_mark"],
  ["laptop", "computer"],
]) {
  if (!glyphs.has(alias) && glyphs.has(name)) glyphs.set(alias, glyphs.get(name)!);
}

// glyph draws an emoji by name, in the skin tone the name carries
// (thumbsup::skin-tone-3, as Slack names it), or :name: for one Lantern does
// not know and for a custom one, which the Emoji component draws as its
// image.
export function glyph(name: string): string {
  const m = /^(.*)::skin-tone-([2-6])$/.exec(name);
  const g = glyphs.get(m ? m[1] : name);
  if (!g) return `:${name}:`;
  return m ? withTone(g, Number(m[2])) : g;
}

const modifierBase = /^\p{Emoji_Modifier_Base}/u;

// withTone puts a skin tone, 2 to 6, on an emoji that takes one, after its
// first character, as Unicode composes them.
function withTone(g: string, tone: number): string {
  const first = String.fromCodePoint(g.codePointAt(0)!);
  if (!modifierBase.test(first)) return g;
  return first + String.fromCodePoint(0x1f3fb + tone - 2) + g.slice(first.length).replace(/^\uFE0F/, "");
}

// takesTone says whether an emoji can be drawn in a skin tone.
export function takesTone(name: string): boolean {
  const g = glyphs.get(name);
  return !!g && modifierBase.test(String.fromCodePoint(g.codePointAt(0)!));
}

const toneKey = "lantern.skinTone";

// skinTone is the tone the picker gives emoji that take one: 1 for none,
// or 2 to 6, kept for this browser.
export function skinTone(): number {
  try {
    const n = Number(localStorage.getItem(toneKey));
    return n >= 2 && n <= 6 ? n : 1;
  } catch {
    return 1;
  }
}

export function setSkinTone(n: number) {
  try {
    if (n >= 2 && n <= 6) localStorage.setItem(toneKey, String(n));
    else localStorage.removeItem(toneKey);
  } catch {
    // A convenience; the picker still works.
  }
}

// toned is an emoji's name in a skin tone, when it takes one.
export function toned(name: string, tone = skinTone()): string {
  return tone > 1 && takesTone(name) ? `${name}::skin-tone-${tone}` : name;
}

// The workspace's custom emoji by name; the session keeps them current. A
// built-in emoji's name always means the built-in one.
let custom = new Set<string>();

export function setCustom(names: string[]) {
  custom = new Set(names.filter((n) => !glyphs.has(n)));
}

export function isCustom(name: string): boolean {
  return custom.has(name);
}

export function customNames(): string[] {
  return [...custom];
}

export function known(name: string): boolean {
  return glyphs.has(name) || custom.has(name);
}

// builtIn says whether a name is one of Lantern's own emoji, or another
// name for one.
export function builtIn(name: string): boolean {
  return glyphs.has(name);
}

// search finds emoji whose name or words start with, or contain, the
// query, custom ones among them.
export function search(query: string, limit = 64): Emoji[] {
  const q = query.toLowerCase().trim().replace(/^:|:$/g, "");
  if (!q) return [];
  const all = [...emoji, ...[...custom].map((name) => ({ name, glyph: "", words: "custom" }) as Emoji)];
  // Names first, then their other names, then the words they are found by.
  const score = (e: Emoji) => {
    const names = [e.name, ...(e.others ?? [])];
    if (names.includes(q)) return 0;
    if (e.name.startsWith(q)) return 1;
    if (names.some((n) => n.startsWith(q))) return 2;
    if (e.words.split(" ").some((w) => w.startsWith(q))) return 3;
    if (names.some((n) => n.includes(q)) || e.words.includes(q)) return 4;
    return 5;
  };
  return all
    .map((e, i) => ({ e, s: score(e), i }))
    .filter((x) => x.s < 5)
    .sort((a, b) => a.s - b.s || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.e);
}

const recentKey = "lantern.recentEmoji";

// recent is this browser's most used reactions, newest first.
export function recent(): string[] {
  try {
    const list = JSON.parse(localStorage.getItem(recentKey) ?? "[]");
    return Array.isArray(list) ? list.filter((n) => typeof n === "string" && known(n)).slice(0, 16) : [];
  } catch {
    return [];
  }
}

export function noteUsed(name: string) {
  try {
    localStorage.setItem(recentKey, JSON.stringify([name, ...recent().filter((n) => n !== name)].slice(0, 16)));
  } catch {
    // Private windows may refuse; recents are a convenience.
  }
}
